import { cn } from '@/lib/utils';

/**
 * Minimum data a caller needs to render a product inline with its role
 * chip. Centralised here so the SOW page, the retainer line form, and any
 * future consumer agree on the shape — and on the three states the role
 * code can be in:
 *
 *   string    — resolved; ProductLabel renders the chip.
 *   null      — resolved but the role is unknown (deactivated, missing
 *               from the catalog); ProductLabel renders no chip.
 *   undefined — still loading; ProductLabel renders a placeholder skeleton
 *               chip so the row doesn't flicker from chipless to chipped
 *               when the role data lands a few frames after the products.
 */
export interface ProductDisplayInfo {
  name: string;
  serviceRoleCode: string | null | undefined;
  /** The product's primary service role. Absent when the product itself
   *  didn't resolve. Lets a T&M card file each activity under its role's
   *  rate row. */
  serviceRoleId?: string;
}

/**
 * Minimum product shape `ProductDisplayLookup` can resolve directly,
 * without needing the id to be in the caller's pre-fetched map. Lets a
 * consumer that already has the full Product object in hand (e.g. fresh
 * from `BulkAddProductsSheet` before the page-level products query
 * refetches) bypass the id-based lookup and still get the role chip
 * resolved correctly.
 *
 * Structural subset of `@clockwise/clients/products` `Product` so we don't
 * tightly couple the generic component to that specific type.
 */
export interface ProductLookupSource {
  id: string;
  name: string;
  serviceRoleId: string;
}

/**
 * Resolves a product reference to display info. Accepts EITHER:
 *
 *   - a productId (string): the lookup checks its pre-fetched map; if
 *     the id isn't there, it returns a synthesised fallback
 *     (`"Product abcd1234…"`, `serviceRoleCode: null`) so consumers never
 *     deal with `undefined` for the product itself.
 *
 *   - a `ProductLookupSource` object: the lookup uses the supplied name
 *     directly and resolves the role chip via the source's
 *     `serviceRoleId`. Use this when the consumer holds the canonical
 *     product (e.g. a freshly-picked product from BulkAddProductsSheet
 *     that hasn't been committed to the page draft yet) and wants the
 *     row to render correctly without waiting for the page's products
 *     query to refetch.
 *
 * `undefined` on `serviceRoleCode` still means "role data is still
 * loading" (see ProductDisplayInfo above); the two states are independent.
 */
export type ProductDisplayLookup = (source: string | ProductLookupSource) => ProductDisplayInfo;

/**
 * Sky-tinted chip showing a service role's short code. Reused wherever
 * the role needs to be surfaced inline — the autocomplete dropdown's
 * fixed-width chip column, the ProductLabel composition below, the SOW
 * card body's distinct-roles list, etc.
 *
 * `data-testid="service-role-code"` is part of the public contract — the
 * product-autocomplete test asserts it. Don't rename.
 *
 * `max-w-[5rem] truncate` keeps the chip bounded even when shortCode hits
 * its 20-char schema cap, so it never pushes the product name off-screen
 * in narrow containers (retainer dialog row, tooltip <li>, etc.).
 */
export function ServiceRoleChip({
  code,
  className,
  // Defaults to the product reading — the chip started life prefixing a
  // product name, where the role IS that product's primary role. Callers
  // rendering a role in its own right (ServiceRoleLabel) say so instead,
  // rather than claiming a "primary role" relationship that has no product
  // on the other end of it.
  title = `Primary role: ${code}`,
}: {
  code: string;
  className?: string;
  title?: string;
}) {
  return (
    <span
      data-testid="service-role-code"
      title={title}
      // `leading-none` so the pill's height is its own (14px) rather than the
      // surrounding text's line-height — 10px type in a 20px line made a tall
      // pill that sat off-centre next to the name. `inline-block`, not
      // `inline-flex`, so `truncate`'s ellipsis actually applies. And
      // `align-middle` because an `overflow: hidden` inline box otherwise
      // sits on the text baseline by its BOTTOM edge, riding high wherever
      // the chip is in inline flow (the week grid's activity cell, the
      // combobox trigger) — inside a flex row it is a no-op.
      className={cn(
        'inline-block max-w-[5rem] shrink-0 truncate rounded-full bg-sky-100 px-1.5 py-0.5 align-middle text-[10px] font-medium uppercase leading-none tracking-wide text-sky-900 dark:bg-sky-900/40 dark:text-sky-100',
        className,
      )}
    >
      {code}
    </span>
  );
}

/**
 * Skeleton placeholder shown in lieu of a real chip while role data is in
 * flight. Same footprint as ServiceRoleChip so the row doesn't shift when
 * the real chip swaps in.
 */
export function ServiceRoleChipSkeleton() {
  return (
    <span
      aria-hidden="true"
      data-testid="service-role-code-skeleton"
      className="inline-block h-3.5 w-10 shrink-0 animate-pulse rounded-full bg-muted align-middle"
    />
  );
}

/**
 * Inline "service-role chip + product name" pair, used wherever a product
 * is rendered in running text or a list row.
 *
 * Product names are routinely duplicated across service roles (e.g. two
 * "Annual review" products under "CFO" and "Audit"). Without the role chip
 * the user can't tell them apart — so we prefix the name with a short-code
 * chip when one is available, render a skeleton while role data is
 * loading, and fall back to the bare name when the role is permanently
 * unresolvable (null).
 *
 * Pure presentational; the caller resolves the product → role-code mapping
 * and passes it in. The chip itself lives in ServiceRoleChip so callers
 * that need a fixed-width chip cell (e.g. the autocomplete dropdown) can
 * use the chip directly without going through ProductLabel.
 */
export function ProductLabel({
  name,
  serviceRoleCode,
  className,
  truncate = true,
}: {
  /** The product's display name (`product.name`). */
  name: string;
  /** Primary service-role short code. See ProductDisplayInfo for state
   *  semantics (string / null / undefined). */
  serviceRoleCode?: string | null;
  className?: string;
  /** Truncate the name with ellipsis at the container width. On by
   *  default so the label drops into tight rows (sheet list items, table
   *  cells, tooltips) without breaking layout. Pass `false` for headings
   *  or anywhere the parent already controls width. */
  truncate?: boolean;
}) {
  return (
    // `inline-flex min-w-0 items-center gap-1.5` is applied LAST in the cn
    // chain so a caller's className can't accidentally swap out the
    // display utility and break the inner name span's truncation (which
    // relies on the name being a flex item with min-width: 0). tailwind-
    // merge keeps the later layout classes winning on conflict.
    <span className={cn(className, 'inline-flex min-w-0 items-center gap-1.5')}>
      {serviceRoleCode === undefined ? (
        <ServiceRoleChipSkeleton />
      ) : serviceRoleCode ? (
        <ServiceRoleChip code={serviceRoleCode} />
      ) : null}
      {/* `block min-w-0` on the inner name guarantees truncation works
       *  even in flex/grid parents that don't establish a min-width-0
       *  chain — `overflow: hidden` (from `truncate`) is a no-op on
       *  inline elements, so we force block here. */}
      <span className={cn('block min-w-0', truncate && 'truncate')}>{name}</span>
    </span>
  );
}
